import clsx from "clsx";
import React, { useState } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import { Tooltip } from "web/react/emo/tooltip";
import useCopyToClipboard from "web/react/hooks/use-copy-to-clip-board";
import {
    BuyOptionPromotionSerializer,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { getUrl } from "web/script/routing/getUrl";
import * as styles from "./promotion.css";

export interface PromotionProps {
    promotion: Pick<BuyOptionPromotionSerializer, "message" | "promoCode">;
}

export function Promotion({ promotion }: PromotionProps): React.ReactElement {
    const { message, promoCode } = promotion;

    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [containerTop, setContainerTop] = useState<number | null>(null);
    const [containerLeft, setContainerLeft] = useState<number | null>(null);

    const { parentRef, isOverlay } = useInStockProductPageContext();

    const [isCodeCopied, setIsCodeCopied] = useState<boolean>(false);
    const copy = useCopyToClipboard();

    function onCopy(): void {
        analytics.event("promo-code", "code_clicked", "pdp");
        if (promoCode) {
            copy(promoCode, "pdp");
            setIsCodeCopied(true);
        }
    }

    function onShow(): void {
        if (isOverlay) {
            const lazyModal = document.querySelector("#lazy-modal");
            if (lazyModal) {
                setContainerTop(lazyModal.getBoundingClientRect().top - lazyModal.scrollTop);
                setContainerLeft(lazyModal.getBoundingClientRect().left - lazyModal.scrollLeft);
            }
        }
    }

    return (
        <div className={styles.container}>
            <div
                className={clsx(styles.message, { [styles.messagePromoCode]: promoCode })}
                onClick={onCopy}
            >
                <SVGIcon name="tag-outline" className={styles.tagIcon} />
                <p className={styles.text}>
                    <Text as="span" textStyle="body-xs">
                        {message}
                    </Text>
                    <button
                        className={styles.infoButton}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        <Tooltip
                            href={getUrl("helpTermsAndConditions") || "/help/terms-and-conditions/"}
                            text={gettext("in_stock_product.lyst_promotion_terms_link")}
                            offset={14}
                            show={showTooltip}
                            onShow={onShow}
                            parentRef={parentRef || null}
                            containerTop={containerTop}
                            containerLeft={containerLeft}
                        >
                            <SVGIcon name="info-icon" className={styles.infoIcon} />
                        </Tooltip>
                    </button>
                </p>
            </div>
            {promoCode && (
                <button onClick={onCopy} className={styles.copyButton}>
                    <Text textStyle="body-xs">
                        {isCodeCopied
                            ? gettext("promotion.copied")
                            : gettext("promotion.copy_code")}
                    </Text>
                </button>
            )}
        </div>
    );
}
